import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  UncontrolledDropdown,
} from "reactstrap";

import { COLOR_PRIMARY } from "../../../constants/theme";
import { useIntl } from "react-intl";
import "emoji-mart/css/emoji-mart.css";

import { EmojiForm } from "../EmojiForm";
import AttachmentView from "./web/AttachmentsView/AttachmentView";
import { Attachment } from "../../../cores/conversations/interfaces/attachment/attachment.interface";
import { uniqueId } from "lodash";
import { ContentMessageType } from "../../../cores/socialChat/types/whatsapp-message.types";
import { beforeUploadFile } from "../../../cores/utils";
import { useWebChatCore } from "../../../cores/conversations/index";
import { useTemplateCore } from "../../../cores/templates";
import { Position } from "../../../cores/globals/interfaces/globalEntity";
import { TEMPLATE_CHAT_FORM } from "../../../constants/modal.constant";
import { useGlobalCore } from "../../../cores/globals";
import Handlebars from "handlebars";
import { useMeCore } from "../../../cores/me";
import { Conversation as SocialConversation } from "../../../cores/socialChat/interfaces/conversations.interface";
import { Conversation } from "../../../cores/conversations/interfaces/api/get-conversations";
import { CHAT_MESSAGES_SENDING } from "../../../constants/loading.constant";
import { LoadingSpinnerWembii } from "../LoadingSpinnerWembii";
import "./SubmitInput.scss";

const MAX_MESSAGE_LENGTH = 4096;
interface Props {
  onSubmit: (message: string) => void;
  conversation: Conversation | SocialConversation | null;
  disabled?: boolean;
  attachments: Attachment[];
  disableAttachments?: boolean;
}

export default function SubmitInput(props: Props) {
  const intl = useIntl();
  const {
    onSubmit,
    conversation,
    disabled = false,
    attachments,
    disableAttachments,
  } = props;

  const { user } = useMeCore();
  const { chatTemplates } = useTemplateCore();
  const { setAttachments } = useWebChatCore();
  const { isModal, turnOnModal, turnOnLoading, isLoading } = useGlobalCore();
  const [text, setText] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const fileInputRef = useRef<any>();
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const emojiToggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showPicker || disabled) return;
    const closeIfOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        emojiPanelRef.current?.contains(target) ||
        emojiToggleRef.current?.contains(target)
      ) {
        return;
      }
      setShowPicker(false);
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
    };
  }, [showPicker, disabled]);
  const acceptedExtensions =
    ".aac, .mp4, .mpeg, .amr, .ogg, .pdf, .ppt, .doc, .xls, .docx, .pptx, .xlsx, .jpeg, .png";

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let fileIsAccepted: boolean = false;
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = e.target.files;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const documentType = file.type;
        let maxSize = 0;
        if (!file) return;
        if (documentType.includes("audio") || documentType.includes("video")) {
          maxSize = 16000000; // 16MB
        } else if (documentType.includes("image")) {
          maxSize = 5000000; // 5MB
        } else if (
          documentType.includes("application/pdf") ||
          documentType.includes("application/vnd.ms-powerpoint") ||
          documentType.includes("application/msword") ||
          documentType.includes("application/vnd.ms-excel") ||
          documentType.includes(
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ) ||
          documentType.includes(
            "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          ) ||
          documentType.includes(
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          )
        ) {
          maxSize = 100000000; // 100MB
        }

        fileIsAccepted = beforeUploadFile(file, maxSize, []);
        if (fileIsAccepted) {
          const reader = new FileReader();
          const fileType =
            file.type.split("/")[0] === "application"
              ? "document"
              : (file.type.split("/")[0] as ContentMessageType);
          reader.onloadend = () => {
            setAttachments([
              {
                id: uniqueId(),
                file: reader.result,
                alt: file.name,
                type: fileType,
                resource: file,
              },
            ]);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const onFormSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (
      !disabled &&
      text.trim().length > 0 &&
      remainingCharacters >= 0 &&
      !isLoading(CHAT_MESSAGES_SENDING)
    ) {
      turnOnLoading(CHAT_MESSAGES_SENDING);
      onSubmit(text.trim());
      setAttachments([]);
      setText("");
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const onClickTemplate = useCallback(() => {
    if (!disabled) {
      turnOnModal(TEMPLATE_CHAT_FORM, true, Position.LEFT);
    }
  }, [disabled, turnOnModal]);

  const onClickEmoji = useCallback(() => {
    if (!disabled) {
      setShowPicker(!showPicker);
    }
  }, [disabled, showPicker]);

  const selectTemplate = useCallback(
    (template) => {
      const messageTemplate = Handlebars.compile(template.message);
      const tags = {
        nombre_usuario_completo: `${user?.firstName} ${user?.lastName}`,
        nombre_usuario: user?.firstName,
        apellido_usuario: user?.lastName,
        nombre_contacto_completo: conversation?.participants[0].name,
      };

      setText(messageTemplate(tags));
    },
    [user, conversation]
  );

  const iconColor = useCallback(
    (active: boolean) => {
      if (disabled) {
        return "#c2c2c2";
      }
      return active ? COLOR_PRIMARY : "grey";
    },
    [disabled]
  );

  const handleChangeText = ({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(target.value);
  };

  const onPressKeys = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && e.shiftKey) return;
    if (
      e.key === "Enter" &&
      text.trim().length > 0 &&
      remainingCharacters >= 0 &&
      !isLoading(CHAT_MESSAGES_SENDING)
    ) {
      e.preventDefault();
      onFormSubmit();
    }
  };

  const remainingCharacters = useMemo(
    () =>
      MAX_MESSAGE_LENGTH -
      text.length -
      (text.match(/\r?\n/g) || []).length * 2,
    [text]
  );

  const countClass = useMemo(() => {
    if (remainingCharacters < 0) return "chat-composer__count--danger";
    if (remainingCharacters < 200) return "chat-composer__count--warn";
    return "chat-composer__count--ok";
  }, [remainingCharacters]);

  const canSend =
    !disabled &&
    text.trim().length > 0 &&
    remainingCharacters >= 0 &&
    !isLoading(CHAT_MESSAGES_SENDING);

  return (
    <div
      className={cn("chat-composer", {
        "chat-composer--picker-open": showPicker && !disabled,
      })}
    >
      {attachments.length > 0 && !disableAttachments && (
        <AttachmentView attachments={attachments} />
      )}
      <Form
        onSubmit={onFormSubmit}
        method="post"
        className="chat-composer__form"
        encType="multipart/form-data"
      >
        <div className="chat-composer__shell">
          <div className="chat-composer__head">
            <span className={cn("chat-composer__count", countClass)}>
              {text.length} / {MAX_MESSAGE_LENGTH}
            </span>
          </div>
          <div className="chat-composer__body">
            <div className="chat-composer__toolbar">
            {!disableAttachments && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileInputChange}
                  accept={acceptedExtensions}
                />
                <button
                  type="button"
                  className="chat-composer__tool-btn"
                  disabled={disabled}
                  aria-label={intl.formatMessage({
                    id: "app.chat.composer.attach",
                  })}
                  onClick={() => !disabled && fileInputRef?.current?.click()}
                >
                  <FontAwesomeIcon
                    icon={faPaperclip}
                    size="lg"
                    color={iconColor(attachments.length > 0)}
                  />
                </button>
              </>
            )}
            <button
              ref={emojiToggleRef}
              type="button"
              className="chat-composer__tool-btn"
              disabled={disabled}
              aria-label="Emoji"
              aria-expanded={showPicker}
              onClick={onClickEmoji}
            >
              <FontAwesomeIcon
                icon={["far", "smile-beam"]}
                color={iconColor(showPicker)}
                size="lg"
              />
            </button>
            <UncontrolledDropdown disabled={disabled}>
              <DropdownToggle
                className="chat-composer__tool-btn"
                tag="button"
                type="button"
                caret={false}
                disabled={disabled}
              >
                <FontAwesomeIcon
                  icon={["fas", "clipboard-list"]}
                  size="lg"
                  color={iconColor(isModal(TEMPLATE_CHAT_FORM))}
                />
              </DropdownToggle>
              <DropdownMenu
                right
                className="border-0 py-3 px-2 shadow option-menu"
              >
                <DropdownItem onClick={onClickTemplate}>
                  <div className="d-flex align-items-center">
                    {intl.formatMessage({
                      id: "app.sendEmail.template.manage",
                    })}
                  </div>
                </DropdownItem>
                <DropdownItem divider />
                {chatTemplates.length === 0 && (
                  <DropdownItem>
                    <div className="d-flex align-items-center text-center text-muted">
                      {intl.formatMessage({ id: "app.sendEmail.noTemplate" })}
                    </div>
                  </DropdownItem>
                )}
                {chatTemplates.map((template) => (
                  <DropdownItem
                    key={template.id}
                    onClick={() => selectTemplate(template)}
                  >
                    <div className="d-flex align-items-center">
                      {template.name}
                    </div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
            </div>

            <div className="chat-composer__field">
            <textarea
              className="chat-composer__textarea"
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "app.chatFacebook.writeMsg",
              })}
              value={text}
              onKeyDown={onPressKeys}
              maxLength={4096}
              onChange={handleChangeText}
              rows={1}
              aria-label={intl.formatMessage({
                id: "app.chatFacebook.writeMsg",
              })}
            />
            </div>

            <div className="chat-composer__side">
            {!isLoading(CHAT_MESSAGES_SENDING) ? (
              <button
                type="submit"
                className="btn chat-composer__send"
                disabled={!canSend}
                aria-label={intl.formatMessage({
                  id: "app.chat.composer.send",
                })}
              >
                <FontAwesomeIcon icon={["far", "paper-plane"]} size="lg" />
              </button>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center chat-composer__send"
                style={{ boxShadow: "none" }}
              >
                <LoadingSpinnerWembii active size="xs" />
              </div>
            )}
            </div>
          </div>
        </div>

        {showPicker && !disabled && (
          <div ref={emojiPanelRef} className="chat-composer__emoji-panel">
            <EmojiForm
              onAdding={(emoji: { native: string }) => {
                setText((prev) =>
                  prev ? `${prev} ${emoji.native}` : emoji.native
                );
                setShowPicker(false);
              }}
            />
          </div>
        )}
      </Form>
    </div>
  );
}
